/**
 * Pointer + keyboard input for the play surface.
 *
 * Pointer Events cover touch, mouse and pen with one code path. A swipe fires
 * the moment it crosses the threshold (not on release) so the lane change feels
 * instant, and the gesture is then locked so one flick never moves two lanes.
 */

export interface InputHandlers {
  onLane: (dir: -1 | 1) => void
  /** Tap / Space / Enter while not running. */
  onPrimary: () => void
}

/** Horizontal travel that turns a drag into a lane change, px. */
const SWIPE_PX = 26
/** Longest press still read as a tap, ms. */
const TAP_MS = 250
/** Largest travel still read as a tap, px. */
const TAP_PX = 12

/** Anything the HUD or an overlay owns — those gestures are never ours. */
const UI_SELECTOR = '[data-ui],button,input,select,textarea,a,label'

interface Gesture {
  id: number
  x: number
  y: number
  /** Which half of the target the pointer went down in. */
  dir: -1 | 1
  t: number
  /** A swipe already fired for this gesture. */
  swiped: boolean
}

/** Returns a detach function. */
export function attachInput(target: HTMLElement, handlers: InputHandlers): () => void {
  let gesture: Gesture | null = null

  // iOS double-tap zoom, long-press callout and text selection all have to go,
  // otherwise a fast player fights the browser instead of the traffic.
  const prev = {
    touchAction: target.style.touchAction,
    userSelect: target.style.userSelect,
    webkitUserSelect: target.style.webkitUserSelect,
    webkitTouchCallout: target.style.getPropertyValue('-webkit-touch-callout'),
  }
  target.style.touchAction = 'none'
  target.style.userSelect = 'none'
  target.style.webkitUserSelect = 'none'
  target.style.setProperty('-webkit-touch-callout', 'none')

  const onPointerDown = (e: PointerEvent): void => {
    if (gesture !== null) return
    if (e.target instanceof Element && e.target.closest(UI_SELECTOR)) return

    const rect = target.getBoundingClientRect()
    gesture = {
      id: e.pointerId,
      x: e.clientX,
      y: e.clientY,
      dir: e.clientX < rect.left + rect.width / 2 ? -1 : 1,
      t: e.timeStamp,
      swiped: false,
    }
    e.preventDefault()
    if (target.setPointerCapture) {
      try {
        target.setPointerCapture(e.pointerId)
      } catch {
        /* pointer already gone */
      }
    }
  }

  const onPointerMove = (e: PointerEvent): void => {
    const g = gesture
    if (!g || g.id !== e.pointerId) return
    e.preventDefault()
    if (g.swiped) return

    const dx = e.clientX - g.x
    const dy = e.clientY - g.y
    if (Math.abs(dx) < SWIPE_PX || Math.abs(dx) <= Math.abs(dy)) return

    g.swiped = true
    handlers.onLane(dx > 0 ? 1 : -1)
  }

  const onPointerUp = (e: PointerEvent): void => {
    const g = gesture
    if (!g || g.id !== e.pointerId) return
    gesture = null
    if (target.hasPointerCapture?.(e.pointerId)) target.releasePointerCapture(e.pointerId)

    if (g.swiped) return
    const travel = Math.hypot(e.clientX - g.x, e.clientY - g.y)
    if (e.timeStamp - g.t > TAP_MS || travel >= TAP_PX) return

    e.preventDefault()
    // The host decides what to act on: a lane change while running, the start
    // of a run otherwise.
    handlers.onLane(g.dir)
    handlers.onPrimary()
  }

  const onPointerCancel = (e: PointerEvent): void => {
    if (!gesture || gesture.id !== e.pointerId) return
    gesture = null
    if (target.hasPointerCapture?.(e.pointerId)) target.releasePointerCapture(e.pointerId)
  }

  const onKeyDown = (e: KeyboardEvent): void => {
    if (e.repeat || e.metaKey || e.ctrlKey || e.altKey) return

    const el = e.target
    if (el instanceof HTMLElement) {
      const tag = el.tagName
      if (el.isContentEditable || tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return
    }

    switch (e.key) {
      case 'ArrowLeft':
      case 'a':
      case 'A':
        e.preventDefault()
        handlers.onLane(-1)
        break
      case 'ArrowRight':
      case 'd':
      case 'D':
        e.preventDefault()
        handlers.onLane(1)
        break
      case ' ':
      case 'Spacebar':
      case 'Enter':
        e.preventDefault()
        handlers.onPrimary()
        break
    }
  }

  target.addEventListener('pointerdown', onPointerDown, { passive: false })
  window.addEventListener('pointermove', onPointerMove, { passive: false })
  window.addEventListener('pointerup', onPointerUp, { passive: false })
  window.addEventListener('pointercancel', onPointerCancel)
  window.addEventListener('keydown', onKeyDown)

  return () => {
    target.removeEventListener('pointerdown', onPointerDown)
    window.removeEventListener('pointermove', onPointerMove)
    window.removeEventListener('pointerup', onPointerUp)
    window.removeEventListener('pointercancel', onPointerCancel)
    window.removeEventListener('keydown', onKeyDown)

    if (gesture && target.hasPointerCapture?.(gesture.id)) {
      target.releasePointerCapture(gesture.id)
    }
    gesture = null

    target.style.touchAction = prev.touchAction
    target.style.userSelect = prev.userSelect
    target.style.webkitUserSelect = prev.webkitUserSelect
    if (prev.webkitTouchCallout) {
      target.style.setProperty('-webkit-touch-callout', prev.webkitTouchCallout)
    } else {
      target.style.removeProperty('-webkit-touch-callout')
    }
  }
}
